import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { StructuredModal } from 'components/StructuredModal/StructuredModal';
import type { IProductsWithSoftwarePlan } from 'hooks/useDCSTypes';
import { IProductModel } from 'components/Cart/models';
import { useHistory } from 'react-router';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { useCartContext } from 'components/Cart/hooks/useCartContext';
import { useLocation } from 'react-router-dom';

export interface ISwapCartBundleState {
  bundleId?: string;
  productsWithSoftwarePlan?: IProductsWithSoftwarePlan;
  needsRedirect?: boolean;
  solutionTitle?: string;
  accessories?: IProductModel[];
}

interface ISwapCartBundleModalProps {
  bundleState: [ISwapCartBundleState | undefined, (state?: ISwapCartBundleState) => void];
}

export const SwapCartBundleModal: React.FC<ISwapCartBundleModalProps> = ({ bundleState }) => {
  const [
    {
      bundleId,
      productsWithSoftwarePlan,
      needsRedirect,
      solutionTitle,
      accessories = [],
    } = {} as ISwapCartBundleState,
    setBundleState,
  ] = bundleState;
  const { addBundleById, addProductsWithSoftwarePlan } = useCartContext();
  const { t } = useTranslation();
  const history = useHistory();
  const isOpen = !!(bundleId || productsWithSoftwarePlan);
  const location = useLocation();

  useEffect(() => {
    setBundleState(undefined);
  }, [location]);

  const onClose = () => {
    setBundleState(undefined);
  };

  const onUpdate = () => {
    if (bundleId) {
      addBundleById(bundleId, { needsRedirect, solutionTitle });
    }

    if (productsWithSoftwarePlan) {
      addProductsWithSoftwarePlan(productsWithSoftwarePlan, accessories, { needsRedirect, solutionTitle });
    }
  };

  const onView = () => {
    history.push(redesignV2RoutePath.CART);
  };

  return (
    <StructuredModal
      modalState={[isOpen, onClose]}
      title={t('swap-cart-bundle.title')}
      description={t('swap-cart-bundle.description')}
      actions={[
        {
          label: t('swap-cart-bundle.view-cart'),
          variant: LinkVariant.SECONDARY,
          action: onView,
        },
        {
          label: t('swap-cart-bundle.update-cart'),
          variant: LinkVariant.PRIMARY,
          action: onUpdate,
        },
      ]}
    />
  );
};

SwapCartBundleModal.displayName = 'SwapCartBundleModal';
